import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { EventSessionProvider } from './context/EventSessionProvider';
import { Toaster } from './components/ui/sonner';
import ScrollManager from './components/ScrollManager';
import { GuestRouteBoundary } from './components/GuestRouteBoundary';
import Landing from './pages/LandingV3';
import LandingV2 from './pages/LandingV2';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import CreateEvent from './pages/CreateEvent';
import EventPage from './pages/EventPage';
import GuestAccessPage from './pages/access/GuestAccessPage';
import LensNotePage from './pages/lens-note/LensNotePage';
import CompositionLanguagePage from './pages/debug/CompositionLanguagePage';
import SoakTestPage from './pages/debug/SoakTestPage';
import FailureCapturePage from './pages/debug/FailureCapturePage';
import WallArchaeologyPage from './pages/debug/WallArchaeologyPage';
import OneWordPage from './pages/one-word/OneWordPage';
import OperatorSmokePage from './pages/operator/OperatorSmokePage';
import FilmSharePage from './pages/FilmSharePage';
import Slideshow from './pages/Slideshow';
import AdminPanel from './pages/AdminPanel';
import AdminCompPage from './pages/AdminCompPage';
import PreservedReturnPage from './pages/PreservedReturnPage';
import AdminFeaturedDemosPage from './pages/AdminFeaturedDemosPage';
import AdminDemoFunnel from './pages/AdminDemoFunnel';
import TeachersStart from './pages/teachers/TeachersStart';
import TeachersAuth from './pages/teachers/TeachersAuth';
import ClassroomHome from './pages/classroom/ClassroomHome';
import WorkspacePage from './pages/classroom/WorkspacePage';
import CollectionTimelinePage from './pages/classroom/CollectionTimelinePage';
import ParentSharePage from './pages/classroom/ParentSharePage';
import ParentUnsubscribePage from './pages/classroom/ParentUnsubscribePage';
import TodayStoryPage from './pages/classroom/TodayStoryPage';
import WeekStoryPage from './pages/classroom/WeekStoryPage';
import AssemblyMode from './pages/classroom/AssemblyMode';
import SpreadingPage from './pages/classroom/SpreadingPage';
import AdminGrowth from './pages/AdminGrowth';
import AdminFunnel from './pages/AdminFunnel';
import AdminThumbnails from './pages/AdminThumbnails';
import Year6FunnelDashboard from './pages/Year6FunnelDashboard';
import Year6PocketBoothPage from './pages/Year6PocketBoothPage';
import AdminPushPanel from './pages/AdminPushPanel';
import PromLandingPage from './pages/PromLandingPage';
import PromSetupPage from './pages/PromSetupPage';
import PromReadyPage from './pages/PromReadyPage';
import PromThankYouPage from './pages/PromThankYouPage';
import PromChallengesPage from './pages/PromChallengesPage';
import PromChallengeLeaderboardPage from './pages/PromChallengeLeaderboardPage';
import PromLiveScreen from './pages/PromLiveScreen';
import PromControlPanel from './pages/PromControlPanel';
import Year6LandingPage from './pages/Year6LandingPage';
import Year6ReadyPage from './pages/Year6ReadyPage';
import Year6MissionScreen from './pages/Year6MissionScreen';
import Year6ControlPanel from './pages/Year6ControlPanel';
import Year6CertificatesPage from './pages/Year6CertificatesPage';
import Year6MemoryBookPage from './pages/Year6MemoryBookPage';
import Year6RevealPage from './pages/Year6RevealPage';
import Year6ParentViewPage from './pages/Year6ParentViewPage';
import Year6SuccessPage from './pages/Year6SuccessPage';
import CreatePersonalPage from './pages/CreatePersonalPage';
import PersonalReadyPage from './pages/PersonalReadyPage';
import AdminRoutingPage from './pages/AdminRoutingPage';
import PreCheckout from './pages/PreCheckout';
import UpsellPage from './pages/UpsellPage';
import ThankYouPage from './pages/ThankYouPage';
import GuestbookPage from './pages/GuestbookPage';
import CreateEventPage from './pages/CreateEventPage';
import GuestbookRecorder from './pages/GuestbookRecorder';
import MemorySnapPage from './pages/MemorySnapPage';
import RememberUsPage from './pages/RememberUsPage';
import LiveRememberUsPage from './pages/LiveRememberUsPage';
import FinalNightPage from './pages/FinalNightPage';
import FinalNightComposerPage from './pages/FinalNightComposerPage';
import SilentFilmPage from './pages/SilentFilmPage';
import DocumentaryPage from './pages/DocumentaryPage';
import PersonalMemoryPage from './pages/PersonalMemoryPage';
import RehearsalNotesPage from './pages/RehearsalNotesPage';
import RehearsalNotesIndexPage from './pages/RehearsalNotesIndexPage';
import MemoryWallPage from './pages/MemoryWallPage';
import ArchivePage from './pages/ArchivePage';
import ArtifactStudioPage from './pages/ArtifactStudioPage';
import SharedRecapPage from './pages/SharedRecapPage';
import BeginPage from './pages/BeginPage';
import WallReadyPage from './pages/WallReadyPage';
import MemoryBookMessagePage from './pages/MemoryBookMessagePage';
import LiveBookPage from './pages/LiveBookPage';
import EventPreview from './pages/EventPreview';
import BoothPage from './pages/BoothPage';
import Booth360Page from './pages/Booth360Page';
import EndCardSettingsPage from './pages/EndCardSettingsPage';
import SchoolsPage from './pages/SchoolsPage';
import SpinPocketPage from './pages/SpinPocketPage';
import TemplateEditor from './pages/TemplateEditor';
import InvitesPage from './pages/InvitesPage';
import InviteEditor from './pages/InviteEditor';
import GuestDashboard from './pages/GuestDashboard';
import RSVPPage from './pages/RSVPPage';
import PricingPage from './pages/PricingPage';
import StudioPassPage from './pages/StudioPassPage';
import BoothOSPage from './pages/BoothOSPage';
import BoothPairingPage from './pages/BoothPairingPage';
import StudioPassSuccessPage from './pages/StudioPassSuccessPage';
import MyEvents from './pages/MyEvents';
import LivePage from './pages/LivePage';
import ChallengePage from './pages/ChallengePage';
import TimelineEditor from './pages/TimelineEditor';
import GuestHome from './pages/GuestHome';
import GuestLanding from './pages/GuestLanding';
import PublicShare from './pages/PublicShare';
import GuestGallery from './pages/GuestGallery';
import PhotoDownload from './pages/PhotoDownload';
import LeaderboardPage from './pages/LeaderboardPage';
import EffectsMode from './pages/EffectsMode';
import LoginPage from './pages/LoginPage';
import AuthPopupCallback from './pages/AuthPopupCallback';
import LandingV3 from './pages/LandingV3';
import PocketBooth from './pages/PocketBooth';
import PocketGallery from './pages/PocketGallery';
import PocketFilm from './pages/PocketFilm';
import EventLanding from './pages/EventLanding';
import EventCheckout from './pages/EventCheckout';
// iter217 — Photobooth Hire commercial vertical
import PhotoboothHirePage from './pages/PhotoboothHirePage';import VenuesPage from './pages/VenuesPage';
import VenueDetailPage from './pages/VenueDetailPage';
import PublicEventsPage from './pages/PublicEventsPage';
import PublicEventDetailPage from './pages/PublicEventDetailPage';
// iter219 — Admin Event Galleries (create / upload / publish)
import AdminPublicGalleriesPage from './pages/AdminPublicGalleriesPage';
import AdminPhotoboothMediaPage from './pages/AdminPhotoboothMediaPage';
import AdminBackdropsPage from './pages/AdminBackdropsPage';
import AdminHistoricEventsPage from './pages/AdminHistoricEventsPage';
import AdminHeritagePage from './pages/AdminHeritagePage';
import AdminReviewsPage from './pages/AdminReviewsPage';
import ReviewsPage from './pages/ReviewsPage';
import LeaveAReviewPage from './pages/LeaveAReviewPage';
import EventReviewPage from './pages/EventReviewPage';
import AdminInventoryPage from './pages/AdminInventoryPage';
import AdminBookingsPage from './pages/AdminBookingsPage';
import AdminQuotesPage from './pages/AdminQuotesPage';
import AnnouncementBanner from './components/AnnouncementBanner';
import RegionalPhotoboothHirePage from './pages/RegionalPhotoboothHirePage';
import ProSessionsPage from './pages/ProSessionsPage';
import ProLoginPage from './pages/ProLoginPage';
import SuppliersDirectory from './pages/SuppliersDirectory';
import SupplierProfilePage from './pages/SupplierProfilePage';
import SupplierOnboardPage from './pages/SupplierOnboardPage';
import SupplierDashboardPage from './pages/SupplierDashboardPage';
import MessagesPage from './pages/MessagesPage';
import ChatPage from './pages/ChatPage';
import SupplierCalendarPage from './pages/SupplierCalendarPage';
import SupplierBookingsPage from './pages/SupplierBookingsPage';
import BookingTokenPage from './pages/BookingTokenPage';
import BookingConfirmationPage from './pages/BookingConfirmationPage';
import CrewLandingPage from './pages/crew/CrewLandingPage';
import CrewFeedPage from './pages/crew/CrewFeedPage';
import CrewProfilePage from './pages/crew/CrewProfilePage';
import CrewSavedPage from './pages/crew/CrewSavedPage';
import CrewMessagesPage from './pages/crew/CrewMessagesPage';
import CrewChatPage from './pages/crew/CrewChatPage';
import CrewTrustPage from './pages/crew/CrewTrustPage';
import CrewAdminPage from './pages/crew/CrewAdminPage';
import InternalContractsPage from './pages/internal/InternalContractsPage';
import ParentSnapshotPage from './pages/parent/ParentSnapshotPage';
import SupplierPostOpportunityPage from './pages/crew/SupplierPostOpportunityPage';
import { SupplierCrewDashboard, SupplierApplicantsPage } from './pages/crew/SupplierCrewDashboard';
import SupplierCompliancePage from './pages/SupplierCompliancePage';
import SupplierLeadsPage from './pages/SupplierLeadsPage';
import SupplierPerformancePage from './pages/SupplierPerformancePage';
import SupplierFayresPage from './pages/SupplierFayresPage';
import FayresListPage from './pages/FayresListPage';
import FayreDetailPage from './pages/FayreDetailPage';
import OrganiserOnboardPage from './pages/OrganiserOnboardPage';
import OrganiserFayresPage from './pages/OrganiserFayresPage';
import OrganiserFayreNewPage from './pages/OrganiserFayreNewPage';
import './App.css';

function AppRouter() {
  const location = useLocation();
  // If we're in a popup callback, let the popup callback handle it
  if (location.pathname === '/auth-callback') {
    return (
      <Routes>
        <Route path="/auth-callback" element={<AuthPopupCallback />} />
      </Routes>
    );
  }
  // For any other route with session_id hash, use the standard auth callback
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <>
      <AnnouncementBanner />
      <Routes>
      <Route path="/" element={<EventLanding />} />
      <Route path="/access/:slug" element={<GuestRouteBoundary><GuestAccessPage /></GuestRouteBoundary>} />
      <Route path="/lens-note/:slug" element={<GuestRouteBoundary><LensNotePage /></GuestRouteBoundary>} />
      {/* iter202 — Internal diagnostic route. Hidden from prod nav;
          accessible by direct URL. Surfaces per-profile composition
          + runtime stabilisation telemetry across the camera pipeline. */}
      <Route path="/debug/composition-language" element={<CompositionLanguagePage />} />
      <Route path="/debug/soak-test" element={<SoakTestPage />} />
      <Route path="/debug/failure-capture" element={<FailureCapturePage />} />
      <Route path="/debug/wall-archaeology/:slug" element={<WallArchaeologyPage />} />
      <Route path="/one-word/:slug" element={<GuestRouteBoundary><OneWordPage /></GuestRouteBoundary>} />
      <Route path="/operator/smoke/:slug" element={<OperatorSmokePage />} />
      <Route path="/event" element={<EventLanding />} />
      <Route path="/begin" element={<BeginPage />} />
      <Route path="/wall-ready/:eventId" element={<WallReadyPage />} />
      <Route path="/recap/shared/:token" element={<SharedRecapPage />} />
      <Route path="/home" element={<Landing />} />
      <Route path="/v2" element={<LandingV2 />} />
      <Route path="/v3-preview" element={<LandingV3 />} />
      <Route path="/guestbook" element={<GuestbookPage />} />
      <Route path="/create" element={<CreateEventPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/preserved/:slug" element={<PreservedReturnPage />} />
      <Route path="/suppliers" element={<SuppliersDirectory />} />
      <Route path="/suppliers/:id" element={<SupplierProfilePage />} />
      <Route path="/supplier/onboard" element={<SupplierOnboardPage />} />
      <Route path="/dashboard/supplier" element={<SupplierDashboardPage />} />
      <Route path="/dashboard/supplier/calendar" element={<SupplierCalendarPage />} />
      <Route path="/dashboard/supplier/bookings" element={<SupplierBookingsPage />} />
      <Route path="/book/:token" element={<BookingTokenPage />} />
      <Route path="/book/confirmation/:token" element={<BookingConfirmationPage />} />
      {/* EventDrop Crew ✦ — namespaced under /crew/* with a distinct
          neon palette per product spec. Lives alongside the main
          editorial EventDrop surfaces. */}
      <Route path="/crew" element={<CrewLandingPage />} />
      <Route path="/crew/feed" element={<CrewFeedPage />} />
      <Route path="/crew/profile" element={<CrewProfilePage />} />
      <Route path="/crew/saved" element={<CrewSavedPage />} />
      <Route path="/crew/messages" element={<CrewMessagesPage />} />
      <Route path="/crew/chat/:threadId" element={<CrewChatPage />} />
      <Route path="/crew/trust" element={<CrewTrustPage />} />
      <Route path="/crew/safety" element={<CrewTrustPage />} />
      <Route path="/crew/admin" element={<CrewAdminPage />} />
      <Route path="/internal/contracts" element={<InternalContractsPage />} />
      <Route path="/parent/:token" element={<ParentSnapshotPage />} />
      <Route path="/dashboard/supplier/crew" element={<SupplierCrewDashboard />} />
      <Route path="/dashboard/supplier/crew/new" element={<SupplierPostOpportunityPage />} />
      <Route path="/dashboard/supplier/crew/:opportunityId/applicants" element={<SupplierApplicantsPage />} />
      <Route path="/dashboard/supplier/compliance" element={<SupplierCompliancePage />} />
      <Route path="/dashboard/supplier/leads" element={<SupplierLeadsPage />} />
      <Route path="/dashboard/supplier/performance" element={<SupplierPerformancePage />} />
      <Route path="/dashboard/supplier/fayres" element={<SupplierFayresPage />} />
      <Route path="/fayres" element={<FayresListPage />} />
      <Route path="/fayres/:id" element={<FayreDetailPage />} />
      <Route path="/organiser/onboard" element={<OrganiserOnboardPage />} />
      <Route path="/organiser/fayres" element={<OrganiserFayresPage />} />
      <Route path="/organiser/fayres/new" element={<OrganiserFayreNewPage />} />
      <Route path="/messages" element={<MessagesPage />} />
      <Route path="/chat/:chatId" element={<ChatPage />} />
      <Route path="/studio-pass" element={<StudioPassPage />} />
      <Route path="/booth-os/:event_id" element={<GuestRouteBoundary><BoothOSPage /></GuestRouteBoundary>} />
      <Route path="/booth" element={<BoothPairingPage />} />
      <Route path="/studio-pass/success" element={<StudioPassSuccessPage />} />
      <Route path="/checkout" element={<EventCheckout />} />
      <Route path="/checkout-old" element={<PreCheckout />} />
      {/* iter217 — Photobooth Hire commercial vertical (sibling to
          the editorial memory platform). New routes:
            /photobooth-hire   — main commercial surface
            /venues, /venues/:slug   — venue catalogue + detail
            /events, /events/:slug   — public showcase + detail
          The `/events/:eventId/endcard` editor route below keeps
          its more-specific match (React Router prefers it). */}
      <Route path="/photobooth-hire" element={<PhotoboothHirePage />} />
      <Route path="/venues" element={<VenuesPage />} />
      <Route path="/venues/:slug" element={<VenueDetailPage />} />
      <Route path="/events" element={<PublicEventsPage />} />
      <Route path="/events/:slug" element={<PublicEventDetailPage />} />
      {/* iter232d — customer-facing review page bound to event slug */}
      <Route path="/events/:slug/review" element={<EventReviewPage />} />
      {/* Primary route for the homepage tier CTAs (Essentials/Experience/
          Premium → /precheckout?package=...). `/checkout-old` kept as
          a back-compat alias so any in-flight Stripe redirect or older
          shared link still resolves. */}
      <Route path="/precheckout" element={<PreCheckout />} />
      <Route path="/upsell" element={<UpsellPage />} />
      <Route path="/thank-you" element={<ThankYouPage />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/pro/login" element={<ProLoginPage />} />
      <Route path="/pro" element={<ProSessionsPage />} />
      <Route path="/my-events" element={<MyEvents />} />
      <Route path="/event/:slug/live" element={<LivePage />} />
      <Route path="/event/:slug/spreading" element={<SpreadingPage />} />
      <Route path="/create-event" element={<CreateEvent />} />
      <Route path="/event/:slug" element={<EventPage />} />
      <Route path="/event/:slug/guestbook" element={<GuestRouteBoundary><GuestbookRecorder /></GuestRouteBoundary>} />
      <Route path="/event/:slug/memory-snap" element={<GuestRouteBoundary><MemorySnapPage /></GuestRouteBoundary>} />
      <Route path="/remember-us/:slug" element={<GuestRouteBoundary><RememberUsPage /></GuestRouteBoundary>} />
      <Route path="/live/remember-us/:slug" element={<LiveRememberUsPage />} />
      <Route path="/event/:slug/final-night" element={<GuestRouteBoundary><FinalNightPage /></GuestRouteBoundary>} />
      <Route path="/event/:slug/final-night/compose" element={<GuestRouteBoundary><FinalNightComposerPage /></GuestRouteBoundary>} />
      <Route path="/event/:slug/silent-film" element={<GuestRouteBoundary><SilentFilmPage /></GuestRouteBoundary>} />
      <Route path="/event/:slug/documentary" element={<GuestRouteBoundary><DocumentaryPage /></GuestRouteBoundary>} />
      <Route path="/event/:slug/m/:contribution_id" element={<GuestRouteBoundary><PersonalMemoryPage /></GuestRouteBoundary>} />
      <Route path="/debug/rehearsal-notes" element={<RehearsalNotesIndexPage />} />
      <Route path="/debug/rehearsal-notes/:slug" element={<RehearsalNotesPage />} />
      <Route path="/event/:slug/memory-wall" element={<GuestRouteBoundary><MemoryWallPage /></GuestRouteBoundary>} />
      <Route path="/event/:slug/archive" element={<GuestRouteBoundary><ArchivePage /></GuestRouteBoundary>} />
      <Route path="/event/:slug/artifacts/:family" element={<ArtifactStudioPage />} />
      <Route path="/event/:slug/memory-book-message" element={<GuestRouteBoundary><MemoryBookMessagePage /></GuestRouteBoundary>} />
      <Route path="/event/:slug/live-book" element={<LiveBookPage />} />
      <Route path="/event/:slug/preview" element={<EventPreview />} />
      <Route path="/event/:slug/booth" element={<BoothPage />} />
      <Route path="/event/:slug/360" element={<Booth360Page />} />
      <Route path="/events/:eventId/endcard" element={<EndCardSettingsPage />} />
      <Route path="/schools" element={<SchoolsPage />} />
      <Route path="/spin" element={<SpinPocketPage />} />
      <Route path="/event/:slug/booth/editor" element={<TemplateEditor />} />
      <Route path="/event/:slug/invites" element={<InvitesPage />} />
      <Route path="/event/:slug/invites/editor" element={<InviteEditor />} />
      <Route path="/event/:slug/challenges" element={<GuestRouteBoundary><ChallengePage /></GuestRouteBoundary>} />
      <Route path="/event/:slug/challenges/editor" element={<TimelineEditor />} />
      <Route path="/event/:slug/guest" element={<GuestHome />} />
      <Route path="/event/:slug/guest/gallery" element={<GuestGallery />} />
      <Route path="/event/:slug/leaderboard" element={<LeaderboardPage />} />
      <Route path="/event/:slug/effects" element={<EffectsMode />} />
      <Route path="/event/:slug/pocket-booth" element={<GuestRouteBoundary><PocketBooth /></GuestRouteBoundary>} />
      <Route path="/event/:slug/pocket-gallery" element={<PocketGallery />} />
      <Route path="/event/:slug/pocket-film" element={<PocketFilm />} />
      <Route path="/event/:slug/g/:guestId" element={<GuestRouteBoundary><GuestDashboard /></GuestRouteBoundary>} />
      <Route path="/rsvp/:guestId" element={<RSVPPage />} />
      <Route path="/event/:slug/slideshow" element={<Slideshow />} />
      <Route path="/film/:slug" element={<GuestRouteBoundary><FilmSharePage /></GuestRouteBoundary>} />
      <Route path="/share/:token" element={<PublicShare />} />
      <Route path="/e/:slug" element={<GuestLanding />} />
      <Route path="/admin" element={<AdminPanel />} />
      {/* iter219 — Admin Event Galleries (create / upload / publish) */}
      <Route path="/admin/event-galleries" element={<AdminPublicGalleriesPage />} />
      {/* iter222f — Admin Photobooth Media (booth/package/addon cover photos) */}
      <Route path="/admin/photobooth-media" element={<AdminPhotoboothMediaPage />} />
      <Route path="/admin/backdrops" element={<AdminBackdropsPage />} />
      <Route path="/admin/historic-events" element={<AdminHistoricEventsPage />} />
      <Route path="/admin/heritage" element={<AdminHeritagePage />} />
      <Route path="/admin/reviews" element={<AdminReviewsPage />} />
      <Route path="/reviews" element={<ReviewsPage />} />
      <Route path="/leave-a-review" element={<LeaveAReviewPage />} />
      {/* iter223 — Admin Inventory & Availability */}
      <Route path="/admin/inventory" element={<AdminInventoryPage />} />
      {/* iter224 — Admin Bookings (date-based pipeline) */}
      <Route path="/admin/bookings" element={<AdminBookingsPage />} />
      {/* iter226 — Admin Quotes archive */}
      <Route path="/admin/quotes" element={<AdminQuotesPage />} />
      {/* iter222i — SEO-anchored regional Photobooth Hire landing pages */}
      <Route path="/photobooth-hire-cheshire"     element={<RegionalPhotoboothHirePage regionKey="cheshire" />} />
      <Route path="/photobooth-hire-north-wales"  element={<RegionalPhotoboothHirePage regionKey="north-wales" />} />
      <Route path="/photobooth-hire-liverpool"    element={<RegionalPhotoboothHirePage regionKey="liverpool" />} />
      <Route path="/photobooth-hire-manchester"   element={<RegionalPhotoboothHirePage regionKey="manchester" />} />
      <Route path="/photobooth-hire-wirral"       element={<RegionalPhotoboothHirePage regionKey="wirral" />} />
      <Route path="/photobooth-hire-shropshire"   element={<RegionalPhotoboothHirePage regionKey="shropshire" />} />
      <Route path="/photobooth-hire-lancashire"   element={<RegionalPhotoboothHirePage regionKey="lancashire" />} />
      <Route path="/photobooth-hire-merseyside"   element={<RegionalPhotoboothHirePage regionKey="merseyside" />} />
      <Route path="/admin/comp" element={<AdminCompPage />} />
      <Route path="/admin/featured-demos" element={<AdminFeaturedDemosPage />} />
      <Route path="/admin/demo" element={<AdminDemoFunnel />} />
      <Route path="/teachers/start" element={<TeachersStart />} />
      <Route path="/teachers/auth" element={<TeachersAuth />} />
      {/* Classroom Timeline (Phase 1 MVP) */}
      <Route path="/classroom" element={<ClassroomHome />} />
      <Route path="/classroom/:workspaceId" element={<WorkspacePage />} />
      <Route path="/assembly/:workspaceId/:filmId" element={<AssemblyMode />} />
      <Route path="/classroom/:workspaceId/c/:collectionId" element={<CollectionTimelinePage />} />
      <Route path="/c/:shareCode" element={<ParentSharePage />} />
      <Route path="/c/:shareCode/today" element={<TodayStoryPage />} />
      <Route path="/c/:shareCode/week" element={<WeekStoryPage />} />
      <Route path="/c/:shareCode/c/:collectionId" element={<ParentSharePage />} />
      <Route path="/c/:shareCode/unsubscribe" element={<ParentUnsubscribePage />} />
      <Route path="/admin/growth" element={<AdminGrowth />} />
      <Route path="/admin/funnel" element={<AdminFunnel />} />
      <Route path="/admin/thumbnails" element={<AdminThumbnails />} />
      <Route path="/admin/year6/funnel" element={<Year6FunnelDashboard />} />
      <Route path="/year6/:slug/booth" element={<Year6PocketBoothPage />} />
      <Route path="/admin/growth/push" element={<AdminPushPanel />} />
      <Route path="/prom" element={<Navigate to="/schools" replace />} />
      <Route path="/prom/setup" element={<PromSetupPage />} />
      <Route path="/prom/thank-you" element={<PromThankYouPage />} />
      <Route path="/prom/:slug/ready" element={<PromReadyPage />} />
      <Route path="/prom/:slug/challenges" element={<PromChallengesPage />} />
      <Route path="/prom/:slug/challenges/:challengeId" element={<PromChallengeLeaderboardPage />} />
      <Route path="/prom/:slug/live" element={<PromLiveScreen />} />
      <Route path="/prom/:slug/control" element={<PromControlPanel />} />
      <Route path="/year6" element={<Navigate to="/schools" replace />} />
      <Route path="/year6/:slug/ready" element={<Year6ReadyPage />} />
      <Route path="/year6/:slug/screen" element={<Year6MissionScreen />} />
      <Route path="/year6/:slug/control" element={<Year6ControlPanel />} />
      <Route path="/year6/:slug/certificates" element={<Year6CertificatesPage />} />
      <Route path="/year6/:slug/memory-book" element={<Year6MemoryBookPage />} />
      <Route path="/year6/:slug/reveal" element={<Year6RevealPage />} />
      <Route path="/year6/:slug/view" element={<Year6ParentViewPage />} />
      <Route path="/year6/:slug/success" element={<Year6SuccessPage />} />
      <Route path="/start" element={<CreatePersonalPage />} />
      <Route path="/personal/:slug/ready" element={<PersonalReadyPage />} />
      <Route path="/admin/growth/routing" element={<AdminRoutingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/download/:photoId" element={<PhotoDownload />} />
      <Route path="/auth-callback" element={<AuthPopupCallback />} />
    </Routes>
    </>
  );
}

function App() {
  // Register service worker once + ack push clicks from URL psid param
  React.useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    }
    try {
      const params = new URLSearchParams(window.location.search);
      const psid = params.get('psid');
      if (psid) {
        import('./lib/push').then(({ ackPushClick }) => ackPushClick(psid));
      }
    } catch {}
  }, []);
  return (
    <AuthProvider>
      <EventSessionProvider>
        <BrowserRouter>
          <ScrollManager />
          <AppRouter />
          <Toaster position="top-right" />
        </BrowserRouter>
      </EventSessionProvider>
    </AuthProvider>
  );
}

export default App;
